import React, { useCallback, useMemo, useRef } from 'react'
import { PortraitSelectorDialog } from '@fs/zion-portrait-selector'
import { useGroupsContext } from '@fs/zion-tree-groups'
import type { Person } from '@fs/zion-tree-types'
import { type UseOverlayReturnOptions } from '@fs/zion-ui'
import { mutateFamilyMemberPortraits } from '../../../data-service/useFocusFamilyMembers'
import { mutateFocusPerson } from '../../../data-service/useFocusPerson'

type Props = {
  onAvatarRefreshingChange?: (refreshing: boolean) => void
  overlayProps: UseOverlayReturnOptions
  person: Person | undefined
}

/**
 * The dialog handles loading, uploading, editing, removing, and saving the portrait internally,
 * and closes itself once a change is saved. All this component adds is refreshing the data that
 * renders the portrait — the focus person behind the banner avatar, and the family members lists
 * that show this person as a spouse or child — reporting via onAvatarRefreshingChange while a
 * saved change is still waiting on those refreshes.
 */
export default function PortraitOverlay({ onAvatarRefreshingChange, overlayProps, person }: Props): JSX.Element | null {
  const pid = person?.id
  const { currentTreeId } = useGroupsContext()
  const { close } = overlayProps
  const portraitChangedRef = useRef(false)

  const handlePortraitChanged = useCallback(() => {
    portraitChangedRef.current = true
    onAvatarRefreshingChange?.(true)
  }, [onAvatarRefreshingChange])

  const closeAndRefresh = useCallback(
    (...args: Parameters<UseOverlayReturnOptions['close']>) => {
      close(...args)
      if (!pid) return
      const treeId = currentTreeId || 'PRIVATE'
      const refreshed = mutateFocusPerson(pid, treeId)
      if (portraitChangedRef.current) {
        portraitChangedRef.current = false
        // The portrait also appears in the family members section, where this person is listed as
        // a spouse and as a child, so those lists have to revalidate too
        const familyRefreshed = mutateFamilyMemberPortraits(pid, treeId)
        // Neither the banner avatar nor those lists can show the change until they revalidate, so
        // keep the indicator up until both settle (cleared even if a revalidation fails)
        Promise.all([refreshed, familyRefreshed])
          .finally(() => onAvatarRefreshingChange?.(false))
          .catch(() => undefined)
      }
    },
    [close, currentTreeId, onAvatarRefreshingChange, pid]
  )

  // The dialog puts this object in context, so keep its identity stable across renders
  const overlay = useMemo<UseOverlayReturnOptions>(
    () => ({ ...overlayProps, close: closeAndRefresh }),
    [closeAndRefresh, overlayProps]
  )

  if (!pid) return null

  return (
    <PortraitSelectorDialog
      onPortraitChanged={handlePortraitChanged}
      overlay={overlay}
      personName={person.name}
      pid={pid}
    />
  )
}
